import { useState, type FormEvent } from 'react';
import {
  Button,
  ButtonBase,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import type { SettingsCategoryRow } from '../../types';
import type { Category, CategorySpecification, MachineOption, SpecificationKey } from '../../../planning/types';
import { SPECIFICATION_KEYS, formatSpecificationLabel } from '../../../planning/constants';
import { CATEGORY_MODAL_LABELS as LABELS, SPECIFICATION_LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';
import SearchableCheckboxList from '../../../../components/SearchableCheckboxList';
import OptionModal from '../options/OptionModal';

// Local editing shape for a specification row — `key` can be '' while the row is freshly added
// and not yet assigned a specification, unlike the persisted CategorySpecification shape.
interface SpecificationRow {
  key: SpecificationKey | '';
  required: boolean;
}

function hasKey(spec: SpecificationRow): spec is CategorySpecification {
  return spec.key !== '';
}

interface CategoryModalProps {
  category?: SettingsCategoryRow;
  // Full options catalog — only passed when this modal is opened from CategoriesTab (the
  // settings management flow). Omitted when used as a quick-create sub-modal elsewhere in the
  // app (e.g. MachineFormPage), which keeps this modal in its original name-only form.
  options?: MachineOption[];
  // Every category already known in the org (active list is enough — see the equivalent note
  // on OptionModal's existingOptions), used to block creating a second category with the same
  // name. Passed from every call site that creates categories (CategoriesTab, and the
  // quick-create sub-modal usages in MachineFormPage/DetailActionDialog).
  existingCategories?: Category[];
  // Pre-checks these options when creating a new category (ignored in edit mode, where
  // category.option_ids already drives the initial selection) — used by MachineFormPage's
  // quick-create flow to default-select whichever managed options are already chosen on the
  // machine form.
  initialOptionIds?: string[];
  onClose: () => void;
  onSave: (name: string, optionIds: string[], specifications: CategorySpecification[]) => Promise<boolean>;
  onAddOption?: (name: string, categoryIds: string[]) => Promise<MachineOption | null>;
}

export default function CategoryModal({ category, options, existingCategories, initialOptionIds, onClose, onSave, onAddOption }: CategoryModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(category?.name ?? '');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [optionIds, setOptionIds] = useState<string[]>(category?.option_ids ?? initialOptionIds ?? []);
  const [specifications, setSpecifications] = useState<SpecificationRow[]>(category?.specifications ?? []);
  const [specificationError, setSpecificationError] = useState(false);
  const [search, setSearch] = useState('');
  const [extraOptions, setExtraOptions] = useState<MachineOption[]>([]);
  const [creatingOption, setCreatingOption] = useState(false);

  const isCreate = !category;
  const showOptionsPanel = !!options;
  const showSpecificationsPanel = !!options;
  const allOptions = [...(options ?? []), ...extraOptions];

  const toggleOption = (id: string) => {
    setOptionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Sorted A-Z for display — a freshly added, not-yet-assigned row always sorts last (there's
  // never more than one at a time, see hasEmptyRow below) rather than participating in the
  // alphabetical order. Handlers key off `key` (unique within the array — at most one '' at a
  // time) rather than array index so this sorted copy never needs mapping back to an
  // insertion-order index.
  const sortedSpecifications = [...specifications].sort((a, b) => {
    if (a.key === '') return 1;
    if (b.key === '') return -1;
    return SPECIFICATION_LABELS[a.key].localeCompare(SPECIFICATION_LABELS[b.key], 'nl');
  });

  const hasEmptyRow = specifications.some((s) => s.key === '');
  const usedKeyCount = specifications.filter((s) => s.key !== '').length;
  const canAddSpecification = !hasEmptyRow && usedKeyCount < SPECIFICATION_KEYS.length;

  // Adds a blank row the user must explicitly assign a specification to — never auto-picks one,
  // so "+ Voeg specificatie toe" doesn't silently configure a spec the user didn't choose.
  const addSpecificationRow = () => {
    if (!canAddSpecification) return;
    setSpecifications((prev) => [...prev, { key: '', required: false }]);
  };

  const updateSpecificationKey = (oldKey: SpecificationKey | '', newKey: SpecificationKey) => {
    setSpecifications((prev) => prev.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s)));
    setSpecificationError(false);
  };

  const updateSpecificationRequired = (key: SpecificationKey | '', required: boolean) => {
    setSpecifications((prev) => prev.map((s) => (s.key === key ? { ...s, required } : s)));
  };

  const removeSpecificationRow = (key: SpecificationKey | '') => {
    setSpecifications((prev) => prev.filter((s) => s.key !== key));
    setSpecificationError(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(LABELS.nameRequired);
      return;
    }
    const isDuplicate = existingCategories?.some(
      (c) => c.id !== category?.id && c.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setNameError(LABELS.nameDuplicate);
      return;
    }
    if (hasEmptyRow) {
      setSpecificationError(true);
      return;
    }
    setSaving(true);
    const ok = await onSave(trimmedName, optionIds, specifications.filter(hasKey));
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <>
      <AppDialog
        open
        onClose={onClose}
        maxWidth={showOptionsPanel ? 'sm' : 'xs'}
        fullWidth
        title={isCreate ? LABELS.createTitle : LABELS.editTitle}
        confirmLabel={LABELS.save}
        confirmFormId="category-form"
        confirmDisabled={saving}
        confirmLoading={saving}
        testId="category-modal"
      >
        <form id="category-form" onSubmit={handleSubmit} noValidate>
          <DialogContent className="flex flex-col gap-4">
            <TextField
              label={LABELS.nameLabel}
              value={name}
              onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
              required
              autoFocus
              fullWidth
              className="mt-2"
              error={!!nameError}
              helperText={nameError || undefined}
            />

            {showOptionsPanel && (
              <SearchableCheckboxList
                heading={LABELS.optionsHeading}
                info={LABELS.optionsInfo}
                searchPlaceholder={LABELS.optionSearchPlaceholder}
                search={search}
                onSearchChange={setSearch}
                items={allOptions}
                selectedIds={optionIds}
                onToggle={toggleOption}
                noItemsText={LABELS.noOptionsFound}
                beforeList={
                  onAddOption && (
                    <ButtonBase
                      onClick={() => setCreatingOption(true)}
                      className="w-full flex items-center justify-start gap-1 px-2 py-1.5 rounded text-primary text-base"
                      data-testid="add-option-button"
                    >
                      <AddIcon fontSize="small" />
                      {LABELS.addOptionButton}
                    </ButtonBase>
                  )
                }
              />
            )}

            {showSpecificationsPanel && (
              <div className="rounded-2xl border border-border-strong p-4">
                <p className="font-semibold mb-1 mt-0">{LABELS.specificationsHeading}</p>
                <p className="text-sm text-text-secondary mt-0 mb-4 ">{LABELS.specificationsInfo}</p>

                <div className="flex flex-col gap-4">
                  {sortedSpecifications.map((spec) => {
                    const availableKeys = SPECIFICATION_KEYS.filter(
                      (k) => k === spec.key || !specifications.some((s) => s.key === k)
                    );
                    const rowTestId = spec.key || 'new';
                    return (
                      <div key={rowTestId} className="flex items-center gap-4" data-testid={`spec-row-${rowTestId}`}>
                        <FormControl className="flex-1 min-w-0" error={specificationError && spec.key === ''}>
                          <InputLabel shrink>{LABELS.specificationLabel}</InputLabel>
                          <Select
                            label={LABELS.specificationLabel}
                            displayEmpty
                            value={spec.key}
                            onChange={(e) => updateSpecificationKey(spec.key, e.target.value as SpecificationKey)}
                            inputProps={{ 'aria-label': LABELS.specificationLabel }}
                            renderValue={(value) =>
                              value ? formatSpecificationLabel(value as SpecificationKey) : (
                                <span className="text-text-secondary">{LABELS.specificationPlaceholder}</span>
                              )
                            }
                          >
                            {availableKeys.map((k) => (
                              <MenuItem key={k} value={k} data-testid={`option-spec-${k}`}>
                                {formatSpecificationLabel(k)}
                              </MenuItem>
                            ))}
                          </Select>
                          {specificationError && spec.key === '' && (
                            <FormHelperText>{LABELS.specificationRequired}</FormHelperText>
                          )}
                        </FormControl>

                        <FormControlLabel
                          className="shrink-0 mr-0 pl-1"
                          control={
                            <Checkbox
                              checked={spec.required}
                              onChange={(e) => updateSpecificationRequired(spec.key, e.target.checked)}
                            />
                          }
                          label={LABELS.specRequiredLabel}
                        />

                        <Tooltip title={LABELS.removeSpecification}>
                          <IconButton
                            onClick={() => removeSpecificationRow(spec.key)}
                            aria-label={LABELS.removeSpecification}
                            data-testid={`remove-spec-${rowTestId}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    );
                  })}

                  {canAddSpecification && (
                    <ButtonBase
                      onClick={addSpecificationRow}
                      className="w-full flex items-center justify-start gap-1 px-2 py-1.5 rounded text-primary text-base"
                      data-testid="add-specification-button"
                    >
                      <AddIcon fontSize="small" />
                      {LABELS.addSpecificationButton}
                    </ButtonBase>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit" data-testid="cancel-button">
              {LABELS.cancel}
            </Button>
            <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
              {LABELS.save}
            </SpinnerButton>
          </DialogActions>
        </form>
      </AppDialog>

      {creatingOption && (
        <OptionModal
          showAddedToListHint
          existingOptions={allOptions}
          onClose={() => setCreatingOption(false)}
          onSave={async (optionName) => {
            if (!onAddOption) return false;
            const created = await onAddOption(optionName, []);
            if (!created) return false;
            setExtraOptions((prev) => [...prev, created]);
            setOptionIds((prev) => [...prev, created.id]);
            return true;
          }}
        />
      )}
    </>
  );
}
